<template>
  <div class="invoice-search-dropdown">
    <div class="dropdown-header">Kapcsolódó külföldi számla</div>

    <!-- Selected item display -->
    <div v-if="selectedInvoice" class="selected-item-container">
      <div class="selected-item">
        <div class="selected-left">
          <div class="selected-supplier-name">
            <span class="name-text">{{ selectedInvoice.supplierName }}</span>
            <span class="country-code">{{ getCountryCode(selectedInvoice) }}</span>
          </div>
          <div class="selected-invoice-number">{{ selectedInvoice.invoiceNumber }}</div>
        </div>
        <div class="selected-right">
          <div class="selected-amount">{{ formatAmount(selectedInvoice.totalNetAmount) }} Ft</div>
          <div class="selected-date">{{ getFormattedDate(selectedInvoice) }}</div>
        </div>
      </div>
      <button class="clear-button" @click="clearSelection">
        <span class="clear-icon"><i class="fa fal fa-times"></i></span>
      </button>
    </div>

    <!-- Search input (shown when no item is selected) -->
    <div v-else class="search-container">
      <b-form-input
        v-model="searchQuery"
        placeholder="Keresés a számlák között"
        class="search-input white-input"
        @focus="isOpen = true"
      />
    </div>

    <div v-if="isOpen && !selectedInvoice" class="dropdown-list">
      <!-- Loading state -->
      <div v-if="isLoading" class="loading-state">
        <span class="loading-spinner"></span>
        <span>Számlák betöltése...</span>
      </div>

      <!-- Error state -->
      <div v-else-if="loadError" class="error-state">
        <span>Hiba történt a számlák betöltésekor</span>
        <button class="retry-button" @click="loadInvoices">Újrapróbálás</button>
      </div>

      <!-- Empty state -->
      <div v-else-if="filteredInvoices.length === 0" class="empty-state">
        <span>Nincs megjeleníthető számla</span>
      </div>

      <!-- Invoice list -->
      <template v-else>
        <div
          v-for="invoice in filteredInvoices"
          :key="invoice.id"
          class="invoice-item"
          @click="selectInvoice(invoice)"
        >
          <div class="invoice-left">
            <div class="supplier-name">
              <span class="name-text">{{ invoice.supplierName }}</span>
              <span class="country-code">{{ getCountryCode(invoice) }}</span>
            </div>
            <div class="invoice-number">{{ invoice.invoiceNumber }}</div>
          </div>
          <div class="invoice-right">
            <div class="amount">{{ formatAmount(invoice.totalNetAmount) }} Ft</div>
            <div class="date">{{ getFormattedDate(invoice) }}</div>
          </div>
        </div>

        <div v-if="numberOfNotShownInvoices > 0" class="more-results">
          További {{ numberOfNotShownInvoices }} találat. Pontosítsd a szűrést.
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import {mapGetters} from "vuex";
import Api from "api-client";

export default {
  name: "InvoiceSearchDropdown",

  props: {
    // Optional: allow parent to pass pre-loaded invoices
    initialInvoices: {
      type: Array,
      default: null
    },
    // Optional: allow parent to pass a pre-selected invoice (e.g., from attachedInvoiceDigest)
    initialSelectedInvoice: {
      type: Object,
      default: null
    },
    customsId: {
      type: Number,
      required: true
    }
  },

  data() {
    return {
      searchQuery: "",
      isOpen: false,
      selectedInvoice: null,
      invoices: [],
      numberOfNotShownInvoices: 0,
      isLoading: false,
      loadError: null,
      searchDebounceTimer: null
    };
  },

  computed: {
    filteredInvoices() {
      // Server-side filtering - just return invoices as-is
      return this.invoices;
    },
    ...mapGetters("app", ["getActiveCompany"])
  },

  watch: {
    getActiveCompany: {
      handler(newCompany) {
        if (newCompany?.id) {
          this.loadInvoices();
        }
      },
      immediate: true
    },
    initialSelectedInvoice: {
      handler(newInvoice) {
        if (newInvoice) {
          this.selectedInvoice = newInvoice;
        }
      },
      immediate: true
    },
    searchQuery: {
      handler(newQuery) {
        // Debounce the search to avoid too many API calls
        if (this.searchDebounceTimer) {
          clearTimeout(this.searchDebounceTimer);
        }
        this.searchDebounceTimer = setTimeout(() => {
          this.loadInvoices(newQuery);
        }, 300);
      }
    }
  },

  methods: {
    async loadInvoices(filter = '') {
      if (this.initialInvoices) {
        this.invoices = this.initialInvoices;
        return;
      }

      const companyId = this.getActiveCompany?.id;
      if (!companyId) {
        return;
      }

      this.isLoading = true;
      this.loadError = null;

      try {
        const response = await Api.getInvoicesFotAttachToCustoms(companyId, filter);
        const responseData = response.data;
        
        // Handle response structure: { data: [...], numberOfNotShownInvoices: number }
        if (responseData && Array.isArray(responseData.data)) {
          this.invoices = responseData.data;
          this.numberOfNotShownInvoices = responseData.numberOfNotShownInvoices || 0;
        } else if (Array.isArray(responseData)) {
          // Direct array response
          this.invoices = responseData;
          this.numberOfNotShownInvoices = 0;
        } else if (responseData && responseData.content) {
          // Paginated response
          this.invoices = responseData.content;
          this.numberOfNotShownInvoices = responseData.totalElements ? responseData.totalElements - responseData.content.length : 0;
        } else {
          this.invoices = [];
          this.numberOfNotShownInvoices = 0;
        }
      } catch (error) {
        console.error("Error loading invoices:", error);
        this.loadError = error;
        this.invoices = [];
        this.numberOfNotShownInvoices = 0;
      } finally {
        this.isLoading = false;
      }
    },

    selectInvoice(invoice) {
      // console.log(invoice);
      this.selectedInvoice = invoice;
      this.$emit("invoice-selected", invoice);
      this.isOpen = false;
      this.searchQuery = "";

      Api.attachInvoiceToCustoms(this.customsId, invoice.id)
        .then(() => {
          this.$toast.success({
            title: "Sikeres mentés"
          });
        })
        .catch(error => {
          this.$toast.error({
            title: "Hiba történt"
          });
          console.error("Error attaching invoice:", error);
        });
    },

    clearSelection() {
      const clearedInvoice = this.selectedInvoice;
      this.selectedInvoice = null;
      this.$emit("invoice-cleared", clearedInvoice);

      Api.removeInvoiceFromCustoms(this.customsId)
        .then(() => {
          console.log("Attachment removed successfully");
        })
        .catch(error => {
          console.error("Error removing attachment:", error);
          this.$toast.error({
            title: "Hiba történt"
          });
        });
    },

    formatAmount(amount) {
      if (amount === null || amount === undefined) return '0';
      return new Intl.NumberFormat('hu-HU', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 4
      }).format(amount);
    },

    getCountryCode(invoice) {
      // Use issuerCountry from API response
      if (invoice?.issuerCountry) {
        return invoice.issuerCountry;
      }
      return "";
    },

    getFormattedDate(invoice) {
      // Use dateValue from API response
      if (invoice?.dateValue) {
        return invoice.dateValue;
      }
      if (invoice?.invoiceDate) {
        const date = new Date(invoice.invoiceDate);
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      }
      if (invoice?.issueDate) {
        const date = new Date(invoice.issueDate);
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      }
      return "";
    },

    handleClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.isOpen = false;
      }
    }
  },

  mounted() {
    document.addEventListener("click", this.handleClickOutside);
  },

  beforeDestroy() {
    document.removeEventListener("click", this.handleClickOutside);
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
  }
};
</script>

<style scoped>
.invoice-search-dropdown {
  font-family: inherit;
  width: 100%;
  position: relative;
}

.dropdown-header {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-left: -10px;
}

/* Selected Item Styles */
.selected-item-container {
  display: flex;
  align-items: center;
  background: #FFFFFF;
  border: 1px solid #E0E0E0;
  border-radius: 24px;
  padding: 12px 16px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.08);
}

.selected-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex: 1;
  min-width: 0;
}

.selected-left {
  flex: 1;
  min-width: 0;
}

.selected-supplier-name {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 2px;
}

.selected-supplier-name .name-text {
  font-weight: 600;
  font-size: 14px;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.selected-supplier-name .country-code {
  font-size: 12px;
  color: #9E9E9E;
  flex-shrink: 0;
}

.selected-invoice-number {
  font-size: 12px;
  color: #9E9E9E;
}

.selected-right {
  text-align: right;
  flex-shrink: 0;
  margin-left: 16px;
}

.selected-amount {
  font-weight: 600;
  font-size: 14px;
  color: #333;
  margin-bottom: 2px;
}

.selected-date {
  font-size: 12px;
  color: #9E9E9E;
}

.clear-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin-left: 12px;
  background: transparent;
  border: 1px solid #E0E0E0;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.clear-button:hover {
  background: #F5F5F5;
  border-color: #BDBDBD;
}

.clear-icon {
  font-size: 18px;
  color: #666;
  line-height: 1;
}

/* Search Container Styles */
.search-container {
  position: relative;
  display: flex;
  align-items: center;
}

::v-deep .search-input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #E0E0E0;
  border-radius: 24px;
  font-size: 14px;
  background-color: #FFFFFF !important;
  outline: none;
  transition: border-color 0.2s ease;
}

.search-input:focus {
  border-color: #AEBFC7;
  box-shadow: none;
  background-color: #FFFFFF !important;
}

.search-input::placeholder {
  color: #9E9E9E;
}

/* Dropdown List Styles */
.dropdown-list {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  background: #FFFFFF;
  border-radius: 16px;
  box-shadow: 0px 10px 30px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  max-height: 450px;
  overflow-y: auto;
}

.invoice-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 16px 20px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-bottom: 1px solid #F5F5F5;
}

.invoice-item:last-child {
  border-bottom: none;
}

.invoice-item:hover {
  background-color: #F9F9F9;
}

.invoice-left {
  flex: 1;
  min-width: 0;
}

.supplier-name {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.name-text {
  font-weight: 600;
  font-size: 14px;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.country-code {
  font-size: 12px;
  color: #9E9E9E;
  flex-shrink: 0;
}

.invoice-number {
  font-size: 12px;
  color: #9E9E9E;
}

.invoice-right {
  text-align: right;
  flex-shrink: 0;
  margin-left: 16px;
}

.amount {
  font-weight: 600;
  font-size: 14px;
  color: #333;
  margin-bottom: 4px;
}

.date {
  font-size: 12px;
  color: #9E9E9E;
}

.more-results {
  padding: 16px 20px;
  text-align: right;
  font-size: 13px;
  color: #9E9E9E;
  border-top: 1px solid #F5F5F5;
  background-color: white;
  border-radius: 0 0 16px 16px;
}

/* Loading State Styles */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 20px;
  color: #9E9E9E;
  font-size: 14px;
  gap: 12px;
}

.loading-spinner {
  width: 24px;
  height: 24px;
  border: 2px solid #E0E0E0;
  border-top-color: #333;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Error State Styles */
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 20px;
  color: #D32F2F;
  font-size: 14px;
  gap: 12px;
}

.retry-button {
  padding: 8px 16px;
  background: #FFFFFF;
  border: 1px solid #D32F2F;
  border-radius: 8px;
  color: #D32F2F;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.retry-button:hover {
  background: #D32F2F;
  color: #FFFFFF;
}

/* Empty State Styles */
.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 20px;
  color: #9E9E9E;
  font-size: 14px;
}
</style>
